import React, { useState, useMemo, useEffect, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import { Download, ChevronsUpDown, Search, DollarSign, TrendingUp, Target, BarChart3, Calendar, Filter, Eye, EyeOff, Award, TrendingDown } from "lucide-react";
import Papa from "papaparse";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { formatCurrency, extractAffiliateName, getDefaultDateFilterState } from "../../utils/helpers";
import { COMMISSION_TIERS } from "../../utils/commissionTiers";
import { BRANDS } from "../../utils/constants";
import useSupabaseData from "../../hooks/useSupabaseData";
import useAffiliateData from "../../hooks/useAffiliateData";
import { supabase } from "../../lib/supabase";
import { brandService } from "../../lib/brandService";

// OPTIMIZED: Use the new individual affiliate service for consistent data with dashboard
import { getFormattedAffiliateData, getIndividualAffiliateDailyPerformance, getIndividualAffiliateAds, clearIndividualAffiliateCache } from "../../lib/individualAffiliateService";
import Button from "../ui/Button";
import AdPerformanceTable from "../affiliate-details/ad-performance-table";
import DateRangePicker from "../DateRangePicker";
import { AffiliateHeaderContext } from "../../context/AffiliateHeaderContext";
import SummaryCard from "../SummaryCard";
import CustomSelect from "../ui/CustomSelect";
import Card from "../ui/Card";
import Alert from "../ui/Alert";
import SkeletonCard from "../ui/SkeletonCard";
import SkeletonSummaryCard from "../ui/SkeletonSummaryCard";

const PerformancePage = () => {
  const { affiliateName } = useParams();
  const decodedAffiliateName = decodeURIComponent(affiliateName);
  const setHeaderContent = useContext(AffiliateHeaderContext);

  const { error, pullSummarizedData, pullAdPerformanceForAffiliate, getAvailableDates, getDateRangeSummary, importAdPerformanceDataByDates } = useSupabaseData();

  // CONSISTENCY FIX: Use the same data source as dashboard
  const { getIndividualAffiliateData, getAllAffiliateData } = useAffiliateData();

  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [overallData, setOverallData] = useState(null);
  const [supabaseData, setSupabaseData] = useState(null);
  const [adPerformanceData, setAdPerformanceData] = useState(null);
  const [currentAdPerformanceData, setCurrentAdPerformanceData] = useState(null);

  const [adPerformanceLoading, setAdPerformanceLoading] = useState(true);

  // Progress tracking for batch loading
  const [loadingProgress, setLoadingProgress] = useState({
    message: "",
    percent: 0,
    total: 0,
    estimated: 0,
  });

  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ROWS_PER_PAGE = 10;

  const [isAdPerformanceExpanded, setIsAdPerformanceExpanded] = useState(true);
  const [groupSimilarAds, setGroupSimilarAds] = useState(true); // New state for grouping

  // Date filtering state - use utility function for consistent default date range
  const defaultDateFilter = getDefaultDateFilterState(7);
  const [dateFilterType, setDateFilterType] = useState(defaultDateFilter.dateFilterType);
  const [singleDate, setSingleDate] = useState(defaultDateFilter.singleDate);
  const [dateRangeStart, setDateRangeStart] = useState(defaultDateFilter.dateRangeStart);
  const [dateRangeEnd, setDateRangeEnd] = useState(defaultDateFilter.dateRangeEnd);
  const [brand, setBrand] = useState(BRANDS[0]?.value || "holistic_hercules");
  const [commissionTiers, setCommissionTiers] = useState(COMMISSION_TIERS);
  const [commissionTiersLoading, setCommissionTiersLoading] = useState(false);
  const [commissionTiersError, setCommissionTiersError] = useState(null);

  const [availableDates, setAvailableDates] = useState([]);
  const [dateRangeSummary, setDateRangeSummary] = useState(null);

  // Chart state - follow main date filter
  const [chartGroupBy, setChartGroupBy] = useState("daily");
  const [chartMetric, setChartMetric] = useState("spend");

  // Helper to safely parse numbers
  const parseNum = (val) => {
    const n = parseFloat(val);
    return isNaN(n) ? 0 : n;
  };

  useEffect(() => {
    let isMounted = true;

    const loadCommissionTiers = async () => {
      if (!brand) {
        if (isMounted) {
          setCommissionTiers(COMMISSION_TIERS);
          setCommissionTiersError(null);
        }
        return;
      }

      setCommissionTiersLoading(true);
      setCommissionTiersError(null);

      try {
        const result = await brandService.getCommissionTiers(brand, { includeFuture: false });
        if (!isMounted) {
          return;
        }

        if (result.success && Array.isArray(result.data) && result.data.length > 0) {
          const mapped = result.data
            .map((row) => ({
              minROAS: Number(row.tier_min_roas),
              maxROAS: row.tier_max_roas !== null ? Number(row.tier_max_roas) : null,
              percentage: Number(row.commission_percentage),
              label: row.tier_label || `${(Number(row.commission_percentage) * 100).toFixed(1)}%`,
            }))
            .filter((tier) => !Number.isNaN(tier.minROAS) && tier.minROAS >= 0.8)
            .sort((a, b) => a.minROAS - b.minROAS);

          setCommissionTiers(mapped.length > 0 ? mapped : COMMISSION_TIERS);
        } else {
          setCommissionTiers(COMMISSION_TIERS);
          if (result.error) {
            setCommissionTiersError(result.error);
          }
        }
      } catch (err) {
        if (isMounted) {
          setCommissionTiers(COMMISSION_TIERS);
          setCommissionTiersError(err.message || "Failed to load commission tiers");
        }
      } finally {
        if (isMounted) {
          setCommissionTiersLoading(false);
        }
      }
    };

    loadCommissionTiers();

    return () => {
      isMounted = false;
    };
  }, [brand]);

  const activeCommissionTiers = useMemo(
    () =>
      Array.isArray(commissionTiers) && commissionTiers.length > 0
        ? commissionTiers
        : COMMISSION_TIERS,
    [commissionTiers]
  );

  // Function to group ads by base name (combining original ads with "- Copy" variants) and aggregate metrics with proper tier-based commission
  const groupAdsByAffiliate = useCallback((data) => {
    if (!data || data.length === 0) return [];

    const groupedData = {};

    data.forEach((ad) => {
      const originalAdName = (ad.adName || "").trim();
      if (!originalAdName) return; // Skip if no ad name

      // Extract base name by removing "- Copy" suffix (case insensitive, handles various formats like "- Copy", "- Copy 2", etc.)
      const baseName = originalAdName.replace(/\s*-\s*copy(\s*\d+)?\s*$/i, "").trim();
      const isCopy = /\s*-\s*copy(\s*\d+)?\s*$/i.test(originalAdName);

      // Use the base name as the grouping key
      if (!groupedData[baseName]) {
        groupedData[baseName] = {
          adName: baseName, // Use base name without "- Copy"
          adSpend: 0,
          revenue: 0,
          purchases: 0,
          commission: 0,
          adCount: 0,
          copyCount: 0, // Track how many are copies
          originalAds: [], // Keep track of original ads for reference
          // Keep other properties from first ad
          campaignName: ad.campaignName || "",
          adsetName: ad.adsetName || "",
          brand: ad.brand || "",
          date: ad.date || "",
        };
      }

      // Aggregate the metrics (including pre-calculated commission from finalized table)
      groupedData[baseName].adSpend += parseNum(ad.adSpend);
      groupedData[baseName].revenue += parseNum(ad.revenue);
      groupedData[baseName].purchases += parseNum(ad.purchases);
      groupedData[baseName].commission += parseNum(ad.commission); // Sum pre-calculated commission
      groupedData[baseName].adCount += 1;
      if (isCopy) {
        groupedData[baseName].copyCount += 1;
      }
      groupedData[baseName].originalAds.push(ad);
    });

    // Calculate ROAS for each grouped ad (commission is pre-calculated from finalized table)
    return Object.values(groupedData).map((group) => {
      const roas = group.adSpend > 0 ? group.revenue / group.adSpend : 0;

      // Create display name based on grouping
      let displayName = group.adName;
      if (group.adCount > 1) {
        if (group.copyCount > 0) {
          // Show that original + copies were combined
          const originalCount = group.adCount - group.copyCount;
          displayName = `${group.adName} (${originalCount + group.copyCount} instances: ${originalCount} original${originalCount !== 1 ? "s" : ""}, ${group.copyCount} cop${group.copyCount !== 1 ? "ies" : "y"})`;
        } else {
          // Regular grouping of identical names
          displayName = `${group.adName} (${group.adCount} instances)`;
        }
      }

      // Determine commission tier label based on aggregate ROAS (for display only)
      const commissionRate = group.adSpend > 0 ? group.commission / group.adSpend : 0;
      const commissionLabel = commissionRate >= 0.08 ? "8.0%" :
                             commissionRate >= 0.07 ? "7.0%" :
                             commissionRate >= 0.066 ? "6.6%" :
                             commissionRate >= 0.065 ? "6.5%" :
                             commissionRate >= 0.06 ? "6.0%" :
                             commissionRate >= 0.055 ? "5.5%" :
                             commissionRate > 0 ? `${(commissionRate * 100).toFixed(1)}%` : "0%";

      return {
        adName: displayName,
        adSpend: group.adSpend,
        revenue: group.revenue,
        roas: roas,
        purchases: group.purchases,
        commission: group.commission, // Use pre-calculated commission from finalized table
        adCount: group.adCount,
        copyCount: group.copyCount,
        originalAds: group.originalAds,
        // Include additional properties for compatibility
        campaignName: group.campaignName,
        adsetName: group.adsetName,
        brand: group.brand,
        date: group.date,
        // Commission tier info derived from actual rate
        commissionTier: null, // No longer recalculating tiers
        commissionLabel: commissionLabel,
        commissionBreakdown: null,
      };
    });
  }, [activeCommissionTiers]);

  // OPTIMIZED: Load data using server-side processing for consistent results with dashboard
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        console.log(`🚀 PERFORMANCE-PAGE: Loading optimized data for ${decodedAffiliateName}`);

        // Build date filter for overall data
        const dateFilter = {
          type: dateFilterType,
          singleDate: dateFilterType === "single" ? singleDate : "",
          dateRangeStart: dateFilterType === "range" ? dateRangeStart : "",
          dateRangeEnd: dateFilterType === "range" ? dateRangeEnd : "",
        };
        
        // Exclude today if endDate is today
        let affiliateDateFilter = dateFilter;
        if (dateFilter.type === "range" && dateFilter.dateRangeEnd) {
          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];
          
          if (dateFilter.dateRangeEnd === todayStr) {
            // Exclude today for affiliate data
            const endDateObj = new Date(dateFilter.dateRangeEnd);
            endDateObj.setDate(endDateObj.getDate() - 1);
            const modifiedEndDate = endDateObj.toISOString().split('T')[0];
            
            affiliateDateFilter = {
              ...dateFilter,
              dateRangeEnd: modifiedEndDate
            };
          }
        }

        // Use date-filtered individual affiliate data instead of all-time data
        const individualData = await getIndividualAffiliateData(
          decodedAffiliateName,
          brand,
          affiliateDateFilter
        );
        
        // Format the data to match the expected structure
        const formattedData = {
          name: decodedAffiliateName,
          ads: individualData || [],
          totalAdSpend: individualData?.reduce((sum, ad) => sum + (parseFloat(ad["Ad Spend"]) || 0), 0) || 0,
          totalRevenue: individualData?.reduce((sum, ad) => sum + (parseFloat(ad["Pixel CV"]) || 0), 0) || 0,
          totalCommission: individualData?.reduce((sum, ad) => sum + (parseFloat(ad["Commission"]) || 0), 0) || 0,
          totalPurchases: individualData?.reduce((sum, ad) => sum + (parseInt(ad["Pixel Purchases"]) || 0), 0) || 0,
          adCount: individualData?.length || 0,
          avgROAS: 0, // Will be calculated below
          avgCommissionRate: 0, // Will be calculated below
        };
        
        // Calculate averages
        if (formattedData.totalAdSpend > 0) {
          formattedData.avgROAS = formattedData.totalRevenue / formattedData.totalAdSpend;
          formattedData.avgCommissionRate = formattedData.totalCommission / formattedData.totalAdSpend;
        }

        console.log(`✅ PERFORMANCE-PAGE: Optimized data loaded:`, {
          name: formattedData.name,
          totalCommission: formattedData.totalCommission,
          adCount: formattedData.adCount,
          avgROAS: formattedData.avgROAS,
        });

        // Set the data
        setSupabaseData(formattedData);
        setOverallData(formattedData);

        // SEPARATE AD LOADING: Don't block the main page loading
        console.log(`🚀 PERFORMANCE-PAGE: Main data loaded, starting separate ad performance loading...`);

        // Start ad performance loading in the background (non-blocking)
        loadAdPerformanceData();

        // Load additional data
        const [dates, dateRangeSummary] = await Promise.all([getAvailableDates(), getDateRangeSummary()]);

        setAvailableDates(dates);
        setDateRangeSummary(dateRangeSummary);
      } catch (err) {
        console.error("❌ PERFORMANCE-PAGE: Error loading data:", err);
        // Set empty data on error
        const emptyData = {
          name: decodedAffiliateName,
          ads: [],
          totalAdSpend: 0,
          totalRevenue: 0,
          totalProfit: 0,
          totalCommission: 0,
          totalPurchases: 0,
          adCount: 0,
          commissionBreakdown: {},
          brand: "",
          avgROAS: 0,
          avgCommissionRate: 0,
        };
        setSupabaseData(emptyData);
        setOverallData(emptyData);
      } finally {
        // Main page loading is complete - ad performance loads separately
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [decodedAffiliateName]);

  // SEPARATE AD PERFORMANCE LOADING: Non-blocking background loading with date filtering
  const loadAdPerformanceData = async () => {
    try {
      // Build date filter based on current filter state
      let dateFilter = {
        type: dateFilterType,
        singleDate: dateFilterType === "single" ? singleDate : "",
        dateRangeStart: dateFilterType === "range" ? dateRangeStart : "",
        dateRangeEnd: dateFilterType === "range" ? dateRangeEnd : "",
      };
      
      // Exclude today if endDate is today
      if (dateFilter.type === "range" && dateFilter.dateRangeEnd) {
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        
        if (dateFilter.dateRangeEnd === todayStr) {
          // Exclude today for ad performance data
          const endDateObj = new Date(dateFilter.dateRangeEnd);
          endDateObj.setDate(endDateObj.getDate() - 1);
          const modifiedEndDate = endDateObj.toISOString().split('T')[0];
          
          dateFilter = {
            ...dateFilter,
            dateRangeEnd: modifiedEndDate
          };
        }
      }

      console.log(
        `🔄 PERFORMANCE-PAGE: Loading ad data with date filter:`,
        dateFilter
      );
      setAdPerformanceLoading(true);

      setLoadingProgress({
        message: "Initializing batch loading...",
        percent: 0,
        total: 0,
        estimated: 0,
      });

      const progressCallback = (progress) => {
        setLoadingProgress((prev) => ({
          message: progress.message || prev.message || "Loading...",
          percent: progress.percent || prev.percent || 0,
          total: progress.total || prev.total || 0,
          estimated: progress.estimated || prev.estimated || 0,
          batch: progress.batch || prev.batch || 0,
          loadTime: progress.loadTime || prev.loadTime || 0,
          isComplete: progress.isComplete || false,
        }));
      };

      const allAdsData = await getIndividualAffiliateData(
        decodedAffiliateName,
        brand,
        dateFilter,
        progressCallback
      );

      console.log(
        `✅ PERFORMANCE-PAGE: Filtered ad data loaded! Total ads: ${allAdsData?.length || 0}`
      );
      setAdPerformanceData(allAdsData || []);
      setCurrentAdPerformanceData(allAdsData || []);
    } catch (error) {
      console.error(`❌ PERFORMANCE-PAGE: Error loading ads:`, error);
      setAdPerformanceData([]);
      setCurrentAdPerformanceData([]);
    } finally {
      setAdPerformanceLoading(false);
    }
  };

  useEffect(() => {
    const handleChanges = async () => {
      try {
        // Don't filter if essential data is still loading
        if (!overallData) {
          console.log("⏳ PERFORMANCE-PAGE: Waiting for data to load before filtering");
          return;
        }

        if (dateFilterType === "all") {
          // FIXED: Always reload data for "All Time" to ensure proper date handling
          console.log(`🔄 PERFORMANCE-PAGE: Reloading data for "All Time" filter`);

          await loadAdPerformanceData();

          const allTimeData = await getFormattedAffiliateData(
            decodedAffiliateName,
            brand,
            null, // startDate = null (all time)
            null // endDate = null (all time)
          );

          console.log(`✅ PERFORMANCE-PAGE: All Time data loaded:`, {
            name: allTimeData.name,
            totalCommission: allTimeData.totalCommission,
            totalSpend: allTimeData.totalAdSpend,
            adCount: allTimeData.adCount,
          });

          setSupabaseData(allTimeData);
          // Note: loadAdPerformanceData() already sets currentAdPerformanceData with brand-filtered data
        }

        setIsDataLoading(true);

        if (dateFilterType === "single") {
          console.log(`🔍 PERFORMANCE-PAGE: Debug - Querying for affiliate "${decodedAffiliateName}" on date "${singleDate}"`);

          // First, let's do a raw query to see what data exists
          const { data: rawData, error: rawError } = await supabase.from("ad_performance").select("*").eq("date", singleDate).gt("spend", 0).limit(10);

          console.log(`🔍 PERFORMANCE-PAGE: Raw data for ${singleDate}:`, {
            found: rawData?.length || 0,
            sampleAffiliateNames: rawData?.slice(0, 5).map((row) => row.affiliate_name) || [],
            targetAffiliate: decodedAffiliateName,
          });

          // Use the updated loadAdPerformanceData which handles DateRangePicker state
          await loadAdPerformanceData();

          // Use the SAME data source as Dashboard for consistency (single date)
          const dateFilter = {
            type: "single",
            singleDate: singleDate,
          };

          const affiliateResult = await getAllAffiliateData(brand, dateFilter);

          const matchingAffiliate = affiliateResult?.affiliates?.find((affiliate) => affiliate.name.toLowerCase() === decodedAffiliateName.toLowerCase());

          console.log(`📊 PERFORMANCE-PAGE: Single date filter - got affiliate data for ${singleDate}`, matchingAffiliate);

          setSupabaseData(
            matchingAffiliate || {
              name: decodedAffiliateName,
              ads: [],
              totalAdSpend: 0,
              totalRevenue: 0,
              totalProfit: 0,
              totalCommission: 0,
              totalPurchases: 0,
              adCount: 0,
              commissionBreakdown: {},
              brand: "",
              avgROAS: 0,
              avgCommissionRate: 0,
            }
          );
        } else if (dateFilterType === "range") {
          console.log(`🔍 PERFORMANCE-PAGE: Range filtering ${dateRangeStart} to ${dateRangeEnd}`);

          // First, let's do a raw query to see what data exists
          const { data: rawRangeData, error: rawRangeError } = await supabase.from("ad_performance").select("*").gte("date", dateRangeStart).lte("date", dateRangeEnd).gt("spend", 0).limit(10);

          console.log(`🔍 PERFORMANCE-PAGE: Raw data for ${dateRangeStart} to ${dateRangeEnd}:`, {
            found: rawRangeData?.length || 0,
            sampleAffiliateNames: rawRangeData?.slice(0, 5).map((row) => row.affiliate_name) || [],
            targetAffiliate: decodedAffiliateName,
          });

          // Use the updated loadAdPerformanceData which handles DateRangePicker state
          await loadAdPerformanceData();

          // Use the SAME data source as Dashboard for consistency
          const dateFilter = {
            type: "range",
            dateRangeStart: dateRangeStart,
            dateRangeEnd: dateRangeEnd,
          };

          const affiliateResult = await getAllAffiliateData(brand, dateFilter);

          console.log(`📊 PERFORMANCE-PAGE: getAllAffiliateData returned:`, affiliateResult);

          const matchingAffiliate = affiliateResult?.affiliates?.find((affiliate) => affiliate.name.toLowerCase() === decodedAffiliateName.toLowerCase());

          console.log(`🎯 PERFORMANCE-PAGE: matchingAffiliate for ${decodedAffiliateName}:`, {
            name: matchingAffiliate?.name,
            totalCommission: matchingAffiliate?.totalCommission,
            totalAdSpend: matchingAffiliate?.totalAdSpend,
            totalRevenue: matchingAffiliate?.totalRevenue,
            adCount: matchingAffiliate?.adCount,
            avgROAS: matchingAffiliate?.avgROAS,
          });

          console.log(`📊 PERFORMANCE-PAGE: Range filter - data loaded for ${dateRangeStart} to ${dateRangeEnd}`);

          // Use the same format as Dashboard
          const affiliateData = matchingAffiliate || {
            name: decodedAffiliateName,
            ads: [],
            totalAdSpend: 0,
            totalRevenue: 0,
            totalProfit: 0,
            totalCommission: 0,
            totalPurchases: 0,
            adCount: 0,
            commissionBreakdown: {},
            brand: "",
            avgROAS: 0,
            avgCommissionRate: 0,
          };

          setSupabaseData(affiliateData);
        } else {
          console.log("overallData", overallData);
          const filteredAds = overallData?.ads?.filter?.((ad) => ad.brand === brand) || [];

          // Recalculate totals based on filtered ads
          const recalculatedTotals = filteredAds.reduce(
            (acc, ad) => ({
              totalAdSpend: acc.totalAdSpend + (ad.adSpend || 0),
              totalRevenue: acc.totalRevenue + (ad.revenue || 0),
              totalProfit: acc.totalProfit + (ad.profit || 0),
              totalCommission: acc.totalCommission + (ad.commission || 0),
              totalPurchases: acc.totalPurchases + (ad.purchases || 0),
              adCount: acc.adCount + 1,
            }),
            {
              totalAdSpend: 0,
              totalRevenue: 0,
              totalProfit: 0,
              totalCommission: 0,
              totalPurchases: 0,
              adCount: 0,
            }
          );

          setSupabaseData({
            ...overallData,
            ads: filteredAds,
            ...recalculatedTotals,
            avgROAS: recalculatedTotals.totalAdSpend > 0 ? recalculatedTotals.totalRevenue / recalculatedTotals.totalAdSpend : 0,
            avgCommissionRate: recalculatedTotals.totalAdSpend > 0 ? recalculatedTotals.totalCommission / recalculatedTotals.totalAdSpend : 0,
          });
        }
        setIsDataLoading(false);
      } catch (err) {
        console.log("Error loading data:", err.message);
      } finally {
        setIsDataLoading(false);
      }
    };

    handleChanges();
  }, [dateFilterType, singleDate, dateRangeStart, dateRangeEnd, brand, overallData]);

  // Separate useEffect to reload data when date filter changes
  useEffect(() => {
    if (decodedAffiliateName) {
      console.log(
        `🔄 PERFORMANCE-PAGE: Date filter changed, reloading all data...`
      );
      
      // Reload overall data with new date filter
      const reloadOverallData = async () => {
        try {
          setIsDataLoading(true);
      
          const dateFilter = {
            type: dateFilterType,
            singleDate: dateFilterType === "single" ? singleDate : "",
            dateRangeStart: dateFilterType === "range" ? dateRangeStart : "",
            dateRangeEnd: dateFilterType === "range" ? dateRangeEnd : "",
          };
          
          // Exclude today if endDate is today
          let affiliateDateFilter = dateFilter;
          if (dateFilter.type === "range" && dateFilter.dateRangeEnd) {
            const today = new Date();
            const todayStr = today.toISOString().split('T')[0];
            
            if (dateFilter.dateRangeEnd === todayStr) {
              // Exclude today for affiliate data
              const endDateObj = new Date(dateFilter.dateRangeEnd);
              endDateObj.setDate(endDateObj.getDate() - 1);
              const modifiedEndDate = endDateObj.toISOString().split('T')[0];
              
              affiliateDateFilter = {
                ...dateFilter,
                dateRangeEnd: modifiedEndDate
              };
            }
          }

          // Use date-filtered individual affiliate data
          const individualData = await getIndividualAffiliateData(
            decodedAffiliateName,
            brand,
            affiliateDateFilter
          );
          
          // Format the data to match the expected structure
          const formattedData = {
            name: decodedAffiliateName,
            ads: individualData || [],
            totalAdSpend: individualData?.reduce((sum, ad) => sum + (parseFloat(ad["Ad Spend"]) || 0), 0) || 0,
            totalRevenue: individualData?.reduce((sum, ad) => sum + (parseFloat(ad["Pixel CV"]) || 0), 0) || 0,
            totalCommission: individualData?.reduce((sum, ad) => sum + (parseFloat(ad["Commission"]) || 0), 0) || 0,
            totalPurchases: individualData?.reduce((sum, ad) => sum + (parseInt(ad["Pixel Purchases"]) || 0), 0) || 0,
            adCount: individualData?.length || 0,
            avgROAS: 0,
            avgCommissionRate: 0,
          };
          
          // Calculate averages
          if (formattedData.totalAdSpend > 0) {
            formattedData.avgROAS = formattedData.totalRevenue / formattedData.totalAdSpend;
            formattedData.avgCommissionRate = formattedData.totalCommission / formattedData.totalAdSpend;
          }
          
          setSupabaseData(formattedData);
          setOverallData(formattedData);
        } catch (err) {
          console.error("❌ Error reloading overall data:", err);
        } finally {
          setIsDataLoading(false);
        }
      };
      
      reloadOverallData();
      loadAdPerformanceData();
    }
  }, [dateFilterType, singleDate, dateRangeStart, dateRangeEnd, brand, decodedAffiliateName]);

  const csvDataFromSupabase = useMemo(() => {
    if (!supabaseData || !supabaseData.ads) return null;

    // Use optimized data format from the individual affiliate service
    return supabaseData.ads.map((row) => ({
      "Ad Name": row.adName || "",
      "Campaign Name": row.campaignName || "",
      "Adset Name": row.adsetName || "",
      "Ad Spend": row.adSpend || 0,
      "Pixel CV": row.revenue || 0,
      ROAS: row.roas || 0,
      "Pixel Purchases": row.purchases || 0,
      Commission: row.commission || 0,
      Date: row.date || "",
      "Commission Tier": row.commissionTier || "",
      Brand: row.brand || "",
    }));
  }, [supabaseData]);

  const handleExport = useCallback(() => {
    if (csvDataFromSupabase) {
      const csv = Papa.unparse(csvDataFromSupabase);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `optimized_performance_${decodedAffiliateName}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [csvDataFromSupabase, decodedAffiliateName]);

  // OPTIMIZED: Calculate daily performance for chart using server-side data
  const dailyPerformance = useMemo(async () => {
    try {
      // Get daily performance from optimized service - use 7 days to match main filter
      const dailyData = await getIndividualAffiliateDailyPerformance(
        decodedAffiliateName,
        brand,
        7 // Always use 7 days to match the main date filter
      );

      return dailyData
        .filter((day) => day.spend > 0 || day.revenue > 0)
        .slice(-7); // Always show last 7 days
    } catch (err) {
      console.error("Error loading daily performance:", err);
      return [];
    }
  }, [decodedAffiliateName, brand]); // Remove chartTimeRange dependency

  // State for daily performance data since we can't use async useMemo directly
  const [dailyPerformanceData, setDailyPerformanceData] = useState([]);

  // Load daily performance data
  useEffect(() => {
    const loadDailyData = async () => {
      try {
        // Calculate days based on the actual date filter selection
        let daysToLoad = 7; // Default fallback
        
        if (dateFilterType === "all") {
          daysToLoad = 365; // Load a year for "all time"
        } else if (dateFilterType === "range" && dateRangeStart && dateRangeEnd) {
          // Calculate actual days in the range
          const startDate = new Date(dateRangeStart);
          const endDate = new Date(dateRangeEnd);
          const timeDiff = endDate.getTime() - startDate.getTime();
          const rangeDays = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
          
          // Use the exact number of days (no extra day needed)
          daysToLoad = rangeDays;

          
          // Limit to prevent API timeouts
          if (daysToLoad > 60) {
            console.log(`⚠️ Large date range (${daysToLoad} days), limiting to 60 days to prevent timeout`);
            daysToLoad = 60;
          }
        } else if (dateFilterType === "single") {
          daysToLoad = 7; // Load more days to ensure we get the specific date
        }
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout after 30 seconds')), 30000)
        );
        
        const dataPromise = getIndividualAffiliateDailyPerformance(
          decodedAffiliateName,
          brand,
          daysToLoad
        );
        
        const dailyData = await Promise.race([dataPromise, timeoutPromise]);

        

        // Filter data based on the date filter type
        let filteredData;
        if (dateFilterType === "all") {
          // For all time, don't filter - use all the data
          filteredData = dailyData;
        } else if (dateFilterType === "single" && singleDate) {
          // For single date, only show that specific date
          filteredData = dailyData.filter(day => day.date === singleDate);
        } else {
          // For range, filter by date range
          filteredData = dailyData.filter(day => {
            return day.date >= dateRangeStart && day.date <= dateRangeEnd;
          });
        }
        

        
        // Calculate how many days to show based on the actual range
        let daysToShow = 7; // Default fallback
        if (dateFilterType === "range" && dateRangeStart && dateRangeEnd) {
          const startDate = new Date(dateRangeStart);
          const endDate = new Date(dateRangeEnd);
          const timeDiff = endDate.getTime() - startDate.getTime();
          daysToShow = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
        } else if (dateFilterType === "all") {
          daysToShow = 365;
        } else if (dateFilterType === "single") {
          daysToShow = 1;
        }
        
        // Show the correct number of days
        let processedData = filteredData.slice(-daysToShow);

        // If no data exists, create placeholder data with zeros for the time range
        if (processedData.length === 0) {
          const timeRangeDays = daysToShow; // Use the calculated days to show
          const today = new Date();
          processedData = [];

          for (let i = timeRangeDays - 1; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(today.getDate() - i);
            processedData.push({
              date: date.toISOString().split("T")[0],
              spend: 0,
              revenue: 0,
              commission: 0,
              purchases: 0,
              adCount: 0,
              roas: 0,
            });
          }
        }

        setDailyPerformanceData(processedData);
      } catch (err) {
        console.error("Error loading daily performance:", err);

        // Even on error, create placeholder data with zeros
        const timeRangeDays = 7; // Always use 7 days to match main filter
        const today = new Date();
        const placeholderData = [];

        for (let i = timeRangeDays - 1; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(today.getDate() - i);
          placeholderData.push({
            date: date.toISOString().split("T")[0],
            spend: 0,
            revenue: 0,
            commission: 0,
            purchases: 0,
            adCount: 0,
            roas: 0,
          });
        }

        setDailyPerformanceData(placeholderData);
      }
    };

    loadDailyData();
  }, [decodedAffiliateName, brand, dateFilterType, dateRangeStart, dateRangeEnd, singleDate]); // Include all date filter dependencies

  // Process chart data based on grouping and time range
  const processedChartData = useMemo(() => {
    if (!dailyPerformanceData.length) return [];

    let groupedData = new Map();

    dailyPerformanceData.forEach((day) => {
      let groupKey;
      let groupLabel;
      let fullLabel;

      // Parse date in local timezone to avoid UTC conversion issues
      const [year, month, dayNum] = day.date.split('-').map(Number);
      const date = new Date(year, month - 1, dayNum);

      if (chartGroupBy === "daily") {
        groupKey = day.date;
        groupLabel = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        fullLabel = date.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } else if (chartGroupBy === "weekly") {
        // Get Monday of the week
        const monday = new Date(date);
        const dayOfWeek = date.getDay();
        const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        monday.setDate(diff);
        groupKey = monday.toISOString().split("T")[0];
        groupLabel = `Week ${monday.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
        fullLabel = `Week of ${monday.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
        })}`;
      } else if (chartGroupBy === "monthly") {
        groupKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        groupLabel = date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
        fullLabel = date.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
      }

      if (!groupedData.has(groupKey)) {
        groupedData.set(groupKey, {
          period: groupKey,
          label: groupLabel,
          fullLabel: fullLabel,
          spend: 0,
          revenue: 0,
          purchases: 0,
          commission: 0,
          adCount: 0,
          dayCount: 0,
          roas: 0,
          value: 0,
        });
      }

      const group = groupedData.get(groupKey);
      group.spend += day.spend;
      group.revenue += day.revenue;
      group.purchases += day.purchases;
      group.commission += day.commission;
      group.adCount += day.adCount;
      group.dayCount += 1;
    });

    // Calculate ROAS and set value based on selected metric
    const result = Array.from(groupedData.values()).map((group) => {
      group.roas = group.spend > 0 ? group.revenue / group.spend : 0;
      group.value = group[chartMetric] || 0;
      return group;
    });

    return result.sort((a, b) => a.period.localeCompare(b.period));
  }, [dailyPerformanceData, chartGroupBy, chartMetric]);

  // Clear cache when needed
  const handleClearCache = useCallback(() => {
    clearIndividualAffiliateCache();
    // Reload the data
    window.location.reload();
  }, [activeCommissionTiers]);

  // Custom Tooltip for Recharts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black backdrop-blur-xl text-white text-sm rounded-xl px-4 py-3 border border-white/30 shadow-2xl">
          <p className="font-semibold text-[#FF6B35] mb-2 text-center">{data.fullLabel}</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
              <span className="text-gray-300">Spend:</span>
            </div>
            <div className="text-green-400 font-medium text-right">{formatCurrency(data.spend)}</div>

            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
              <span className="text-gray-300">Revenue:</span>
            </div>
            <div className="text-blue-400 font-medium text-right">{formatCurrency(data.revenue)}</div>

            <div className="flex items-center">
              <div className="w-2 h-2 bg-purple-400 rounded-full mr-2"></div>
              <span className="text-gray-300">Purchases:</span>
            </div>
            <div className="text-purple-400 font-medium text-right">{data.purchases}</div>

            <div className="flex items-center">
              <div className="w-2 h-2 bg-orange-400 rounded-full mr-2"></div>
              <span className="text-gray-300">Commission:</span>
            </div>
            <div className="text-orange-400 font-medium text-right">{formatCurrency(data.commission)}</div>

            <div className="flex items-center">
              <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
              <span className="text-gray-300">Avg ROAS:</span>
            </div>
            <div className="text-yellow-400 font-medium text-right">{data.roas.toFixed(2)}</div>

            {chartGroupBy !== "daily" && (
              <>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
                  <span className="text-gray-300">Days:</span>
                </div>
                <div className="text-gray-300 font-medium text-right">{data.dayCount || 1}</div>
              </>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate commission tier breakdown
  const commissionTierBreakdown = useMemo(() => {
    if (!currentAdPerformanceData || currentAdPerformanceData.length === 0) {
      return {
        tierBreakdown: [],
        summary: {
          totalAds: 0,
          qualifyingAds: 0,
          averageCommissionRate: 0,
          totalEarnings: 0,
        },
      };
    }

    // Process ads and group by commission tier
    const tierMap = new Map();
    let totalEarnings = 0;
    let totalSpend = 0;
    let qualifyingAds = 0;

    // Initialize all tiers
    const tiersToUse = activeCommissionTiers;

    tiersToUse.forEach((tier) => {
      tierMap.set(tier.label, {
        ...tier,
        adCount: 0,
        totalSpend: 0,
        totalEarnings: 0,
        roasRange: "",
      });
    });

    // Process each ad using pre-calculated commission from finalized table
    currentAdPerformanceData.forEach((ad) => {
      const adSpend = parseNum(ad.adSpend);
      const revenue = parseNum(ad.revenue);
      const adCommission = parseNum(ad.commission); // Pre-calculated from finalized table
      const roas = adSpend > 0 ? revenue / adSpend : 0;

      totalSpend += adSpend;

      if (adCommission > 0 && adSpend > 0) {
        qualifyingAds++;
        totalEarnings += adCommission;

        // Determine which tier this ad falls into based on its actual commission rate
        const actualRate = adCommission / adSpend;
        let matchedTier = null;

        // Find the tier that matches this commission rate
        for (const tier of tiersToUse) {
          if (actualRate >= tier.percentage - 0.001) { // Small tolerance for floating point
            matchedTier = tier;
          }
        }

        // Add to the appropriate tier
        if (matchedTier) {
          const tierData = tierMap.get(matchedTier.label);
          if (tierData) {
            tierData.adCount++;
            tierData.totalSpend += adSpend;
            tierData.totalEarnings += adCommission;
          }
        }
      }
    });

    // Set ROAS ranges for display
    const tiers = Array.from(tierMap.values());
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      const nextTier = tiers[i + 1];
      if (nextTier) {
        tier.roasRange = `${tier.minROAS.toFixed(1)} - ${nextTier.minROAS.toFixed(1)}`;
      } else {
        tier.roasRange = `${tier.minROAS.toFixed(1)} - ∞`;
      }
    }

    // Show all tiers and sort by minROAS (removed filter to display all tiers, even empty ones)
    const activeTiers = tiers.sort((a, b) => a.minROAS - b.minROAS);

    return {
      tierBreakdown: activeTiers,
      summary: {
        totalAds: currentAdPerformanceData.length,
        qualifyingAds,
        averageCommissionRate: totalSpend > 0 ? (totalEarnings / totalSpend) * 100 : 0,
        totalEarnings,
      },
    };
  }, [currentAdPerformanceData, activeCommissionTiers]);

  // Set header content
  useEffect(() => {
    setHeaderContent(
      <div className="flex justify-between items-center w-full">
        <div>
          <h1 className="text-2xl font-bold text-white">Performance Overview & Analytics</h1>
          <p className="text-sm text-gray-300">Track your ad performance and commission earnings</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-[#B8460E] to-[#8B3A0E] rounded-full flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-xs">{decodedAffiliateName.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <p className="text-white font-semibold text-sm">{decodedAffiliateName}</p>
            <p className="text-gray-400 text-xs">Affiliate Partner</p>
          </div>
        </div>
      </div>
    );
    // Cleanup function to reset header when component unmounts
    return () => setHeaderContent(null);
  }, [setHeaderContent, decodedAffiliateName]);

  // Filtered data for search and pagination (with optional grouping)
  const filteredData = useMemo(() => {
    if (!currentAdPerformanceData) return [];

    // First, apply grouping if enabled
    let dataToFilter = groupSimilarAds ? groupAdsByAffiliate(currentAdPerformanceData) : currentAdPerformanceData;

    // Then apply search filter
    if (!searchTerm.trim()) {
      return dataToFilter;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter((row) => row.adName.toLowerCase().includes(searchLower) || row.campaignName?.toLowerCase().includes(searchLower) || row.adsetName?.toLowerCase().includes(searchLower));
  }, [currentAdPerformanceData, searchTerm, groupSimilarAds, groupAdsByAffiliate]);

  // Paginated data for display
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, ROWS_PER_PAGE]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Row 1: Filters & Summary Cards Skeleton (mobile-friendly) */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4 w-full">
          {/* Filters Skeleton */}
          <div className="grid grid-cols-2 gap-3 items-center w-full lg:w-auto">
            {/* Date Range + Brand */}
            <SkeletonCard width="w-full" height="h-10" rounded="rounded-lg" />
            <SkeletonCard width="w-full" height="h-10" rounded="rounded-lg" />
            {/* Export */}
            <div className="col-span-2">
              <SkeletonCard width="w-full" height="h-9" rounded="rounded-lg" />
            </div>
          </div>

          {/* Summary Cards Skeleton */}
          <div className="grid grid-cols-2 sm:flex items-stretch sm:items-center gap-3 w-full lg:w-auto">
            <SkeletonSummaryCard iconBgColor="bg-blue-500/20" />
            <SkeletonSummaryCard iconBgColor="bg-orange-500/20" />
          </div>
        </div>

        {/* Performance Analytics Chart Skeleton */}
        <div className="bg-white/5 border border-white/10 rounded-xl shadow-lg p-4 lg:p-6 backdrop-blur-xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 lg:mb-6">
            <div>
              <div className="w-40 h-5 bg-white/10 rounded animate-pulse mb-2"></div>
              <div className="w-56 h-3.5 bg-white/10 rounded animate-pulse"></div>
            </div>
            {/* Chart Controls Skeleton */}
            <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-row sm:gap-3 mt-3 lg:mt-0 w-full lg:w-auto">
              <div className="h-8 bg-white/10 rounded animate-pulse"></div>
              <div className="h-8 bg-white/10 rounded animate-pulse"></div>
              <div className="h-8 bg-white/10 rounded animate-pulse"></div>
            </div>
          </div>
          {/* Chart Container Skeleton */}
          <div className="h-72 sm:h-80 lg:h-96 bg-white/5 rounded-lg animate-pulse"></div>
          {/* Summary Stats Skeleton */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 mt-4 lg:mt-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white/5 rounded-lg p-2 lg:p-3 border border-white/10">
                <div className="w-16 h-2.5 bg-white/10 rounded animate-pulse mb-2"></div>
                <div className="w-14 h-5 bg-white/10 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Ad Performance Table Skeleton */}
        <div className="bg-white/5 border border-white/10 rounded-xl shadow-lg p-4 lg:p-6 backdrop-blur-xl">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3 lg:gap-4 lg:mb-4">
            <div>
              <div className="w-28 h-5 bg-white/10 rounded animate-pulse mb-2"></div>
              <div className="w-44 h-3.5 bg-white/10 rounded animate-pulse"></div>
            </div>
            <div className="flex items-center gap-2 lg:gap-4 w-full sm:w-auto">
              <div className="w-full sm:w-60 lg:w-72 h-9 bg-white/10 rounded-lg animate-pulse"></div>
              <div className="w-40 h-9 bg-white/10 rounded-lg animate-pulse hidden sm:block"></div>
              <div className="w-32 h-9 bg-white/10 rounded-lg animate-pulse hidden sm:block"></div>
            </div>
          </div>
          {/* Table Skeleton */}
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 lg:gap-4 p-3 lg:p-4 bg-white/5 rounded-lg">
                <div className="w-24 h-3.5 bg-white/10 rounded animate-pulse"></div>
                <div className="w-20 h-3.5 bg-white/10 rounded animate-pulse"></div>
                <div className="w-16 h-3.5 bg-white/10 rounded animate-pulse"></div>
                <div className="w-12 h-3.5 bg-white/10 rounded animate-pulse"></div>
                <div className="w-16 h-3.5 bg-white/10 rounded animate-pulse hidden sm:block"></div>
                <div className="w-12 h-3.5 bg-white/10 rounded animate-pulse hidden sm:block"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Commission Tier Breakdown Skeleton */}
        <div className="bg-white/5 border border-white/10 rounded-xl shadow-lg p-6 backdrop-blur-xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="w-48 h-6 bg-white/10 rounded animate-pulse mb-2"></div>
              <div className="w-64 h-4 bg-white/10 rounded animate-pulse"></div>
            </div>
          </div>

          {/* Commission Tier Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl">
                <div className="w-24 h-4 bg-white/10 rounded animate-pulse mb-2"></div>
                <div className="w-32 h-3 bg-white/10 rounded animate-pulse mb-4"></div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <div className="w-12 h-3 bg-white/10 rounded animate-pulse"></div>
                    <div className="w-8 h-3 bg-white/10 rounded animate-pulse"></div>
                  </div>
                  <div className="flex justify-between">
                    <div className="w-16 h-3 bg-white/10 rounded animate-pulse"></div>
                    <div className="w-20 h-3 bg-white/10 rounded animate-pulse"></div>
                  </div>
                  <div className="flex justify-between">
                    <div className="w-20 h-3 bg-white/10 rounded animate-pulse"></div>
                    <div className="w-16 h-3 bg-white/10 rounded animate-pulse"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Commission Tier Structure Skeleton */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-xl">
            <div className="w-40 h-5 bg-white/10 rounded animate-pulse mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="flex justify-between items-center py-2">
                  <div className="w-32 h-4 bg-white/10 rounded animate-pulse"></div>
                  <div className="w-20 h-4 bg-white/10 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary Statistics Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl text-center">
                <div className="w-16 h-8 bg-white/10 rounded animate-pulse mx-auto mb-2"></div>
                <div className="w-24 h-4 bg-white/10 rounded animate-pulse mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Row 1: Filters & Summary Cards */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4 w-full">
        {/* Filters */}
        <div className="grid grid-cols-2 gap-3 items-center w-full lg:flex lg:flex-row lg:flex-nowrap lg:items-center lg:gap-3 lg:w-auto">
          <div className="w-full max-w-[200px]">
            <DateRangePicker dateFilterType={dateFilterType} setDateFilterType={setDateFilterType} singleDate={singleDate} setSingleDate={setSingleDate} dateRangeStart={dateRangeStart} setDateRangeStart={setDateRangeStart} dateRangeEnd={dateRangeEnd} setDateRangeEnd={setDateRangeEnd} availableDates={availableDates} dateRangeSummary={dateRangeSummary} theme="dark" />
          </div>
          {/* Brand Dropdown - Glass Morphism */}
          <div className="relative group w-full max-w-[200px]">
            <select value={brand} onChange={(e) => setBrand(e.target.value)} className="appearance-none w-full bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl px-5 py-3 pr-10 text-white hover:bg-black/50 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent transition-all duration-300 hover:border-[#B8460E]/50 cursor-pointer shadow-xl text-sm font-medium">
              {BRANDS.map((brandOption) => (
                <option key={brandOption.value} value={brandOption.value} className="bg-gray-900 text-white">
                  {brandOption.label}
                </option>
              ))}
            </select>
            {/* Custom dropdown arrow with glass effect */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
              <div className="w-4 h-4 text-white/70 group-hover:text-[#B8460E] transition-colors duration-300">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {/* Shine effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 pointer-events-none rounded-2xl"></div>
          </div>

          {/* Export Button */}
          <Button onClick={handleExport} variant="outline" className="bg-white/10 text-white hover:bg-white/20 border-white/20 px-3 py-2 text-sm w-full sm:w-auto">
            <Download className="mr-1 h-3 w-3" />
            Export
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:flex items-stretch sm:items-center gap-3 w-full lg:w-auto">
          <SummaryCard title="Total Ad Spend" value={formatCurrency(supabaseData?.totalAdSpend || 0)} icon={<DollarSign className="text-white" />} iconBgColor="bg-blue-500/20" isLoading={isDataLoading} />
          <SummaryCard title="Total Commissions" value={formatCurrency(supabaseData?.totalCommission || 0)} icon={<TrendingUp className="text-white" />} iconBgColor="bg-orange-500/20" isLoading={isDataLoading} />
        </div>
      </div>

      {/* Performance Analytics Chart - Front and Center */}
      <Card className="p-3 sm:p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 lg:mb-6">
          <div>
            <h3 className="text-base lg:text-lg font-semibold text-white mb-2">Performance Analytics</h3>
            <p className="text-xs lg:text-sm text-gray-400">Track your ad spend, revenue, and performance over time</p>
          </div>

          {/* Chart Controls */}
          <div className="flex flex-row gap-3 mt-3 lg:mt-0 w-full lg:w-auto">
            {/* Chart Type Selector */}
            <div className="flex items-center space-x-2">
              <label className="hidden sm:inline text-xs lg:text-sm text-gray-400 whitespace-nowrap">Group by:</label>
              <CustomSelect
                value={chartGroupBy}
                onChange={setChartGroupBy}
                options={[
                  { value: "daily", label: "Daily" },
                  { value: "weekly", label: "Weekly" },
                  { value: "monthly", label: "Monthly" },
                ]}
              />
            </div>

            {/* Metric Selector */}
            <div className="flex items-center space-x-2">
              <label className="hidden sm:inline text-xs lg:text-sm text-gray-400 whitespace-nowrap">Metric:</label>
              <CustomSelect
                value={chartMetric}
                onChange={setChartMetric}
                options={[
                  { value: "spend", label: "Ad Spend" },
                  { value: "revenue", label: "Revenue" },
                  { value: "purchases", label: "Purchases" },
                  { value: "commission", label: "Commission" },
                  { value: "roas", label: "Avg ROAS" },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Chart Container */}
        <div className="h-72 sm:h-80 lg:h-96 relative">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={processedChartData} margin={{ top: 10, right: 4, left: 8, bottom: 5 }} barSize={30}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#B8460E" stopOpacity={0.8} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis dataKey="label" stroke="rgba(255, 255, 255, 0.5)" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={50} />
              <YAxis
                stroke="rgba(255, 255, 255, 0.5)"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  if (chartMetric === "roas") return value.toFixed(1);
                  if (chartMetric === "purchases") return Math.round(value);
                  if (chartMetric === "commission") {
                    return value > 1000 ? `$${(value / 1000).toFixed(1)}k` : `$${Math.round(value)}`;
                  }
                  return value > 1000 ? `$${(value / 1000).toFixed(0)}k` : `$${Math.round(value)}`;
                }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255, 255, 255, 0.05)" }} />
              <Legend formatter={(value, entry, index) => <span className="text-white">{value}</span>} />
              <Bar dataKey="value" name={chartMetric === "spend" ? "Ad Spend" : chartMetric === "revenue" ? "Revenue" : chartMetric === "purchases" ? "Purchases" : chartMetric === "commission" ? "Commission" : "Avg ROAS"} fill="url(#colorValue)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          {/* Empty State Overlay */}
          {processedChartData.every((d) => d.value === 0) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm rounded-lg">
              <div className="text-center">
                <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-300 mb-2">No Performance Data</h4>
                <p className="text-gray-400 text-sm">
                  Performance data will appear here once ads are running.
                  <br />
                  Hover over the chart to see $0.00 values for each time period.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="text-xs text-gray-400 uppercase tracking-wide">Avg Daily Spend</div>
            <div className="text-lg font-semibold text-green-400">{formatCurrency(dailyPerformanceData.length > 0 ? dailyPerformanceData.reduce((sum, d) => sum + d.spend, 0) / dailyPerformanceData.length : 0)}</div>
          </div>
          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="text-xs text-gray-400 uppercase tracking-wide">Avg Daily Revenue</div>
            <div className="text-lg font-semibold text-blue-400">{formatCurrency(dailyPerformanceData.length > 0 ? dailyPerformanceData.reduce((sum, d) => sum + d.revenue, 0) / dailyPerformanceData.length : 0)}</div>
          </div>
          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="text-xs text-gray-400 uppercase tracking-wide">Avg Daily Commission</div>
            <div className="text-lg font-semibold text-orange-400">{formatCurrency(dailyPerformanceData.length > 0 ? dailyPerformanceData.reduce((sum, d) => sum + d.commission, 0) / dailyPerformanceData.length : 0)}</div>
          </div>
          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="text-xs text-gray-400 uppercase tracking-wide">Avg ROAS</div>
            <div className="text-lg font-semibold text-yellow-400">{dailyPerformanceData.length > 0 ? (dailyPerformanceData.reduce((sum, d) => sum + (d.spend > 0 ? d.revenue / d.spend : 0), 0) / dailyPerformanceData.length).toFixed(2) : "0.00"}</div>
          </div>
          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="text-xs text-gray-400 uppercase tracking-wide">Total Purchases</div>
            <div className="text-lg font-semibold text-purple-400">{dailyPerformanceData.reduce((sum, d) => sum + (d.purchases || 0), 0)}</div>
          </div>
        </div>
      </Card>

      {/* Row 2: Ad Performance Table */}
      <div className="bg-white/5 border border-white/10 rounded-xl shadow-lg p-4 lg:p-6 backdrop-blur-xl">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 lg:gap-4 lg:mb-4">
          <div className="w-full sm:w-auto">
            <h3 className="text-lg lg:text-xl font-bold text-white text-center sm:text-left">Ad Performance</h3>
            <p className="text-xs lg:text-sm text-gray-400 text-center sm:text-left">
              {adPerformanceLoading
                ? loadingProgress.percent > 0 && loadingProgress.total > 0 && loadingProgress.estimated > 0
                  ? `🔄 Loading ads: ${loadingProgress.total.toLocaleString()} of ~${loadingProgress.estimated.toLocaleString()} - ${loadingProgress.percent}% complete`
                  : loadingProgress.total > 0
                    ? `🔄 Loading ads: ${loadingProgress.total.toLocaleString()} ads loaded...`
                    : "🔄 Loading all ads..."
                : currentAdPerformanceData && currentAdPerformanceData.length > 0
                  ? groupSimilarAds
                    ? `${filteredData.length} unique ${filteredData.length === 1 ? "ad" : "ads"} (${currentAdPerformanceData?.length || 0} total instances)${searchTerm ? ` - filtered` : ""} ✅`
                    : `${filteredData.length} individual instances${searchTerm ? ` (filtered from ${currentAdPerformanceData?.length || 0})` : ""} ✅`
                  : "⏳ Ad performance data will load shortly..."}
            </p>
          </div>
          <div className="flex items-center gap-2 lg:gap-4 w-full sm:w-auto">
            <div className="relative w-full sm:w-60 lg:w-72">
              <Search className="w-4 h-4 lg:w-5 lg:h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search ads..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pl-9 pr-3 text-sm lg:text-base text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E]" />
            </div>
            <Button variant="outline" onClick={() => setGroupSimilarAds(!groupSimilarAds)} className={`text-xs lg:text-sm text-white ${groupSimilarAds ? "bg-[#B8460E]/20 border-[#B8460E]/50 hover:bg-[#B8460E]/30" : "bg-white/10 hover:bg-white/20"}`}>
              {groupSimilarAds ? "Show Individual Instances" : "Group Similar Ads"}
            </Button>
            <Button variant="outline" onClick={() => setIsAdPerformanceExpanded(!isAdPerformanceExpanded)} className="text-xs lg:text-sm text-white bg-white/10 hover:bg-white/20">
              {isAdPerformanceExpanded ? "Hide Details" : "Show Details"}
            </Button>
          </div>
        </div>

        {isAdPerformanceExpanded &&
          (adPerformanceLoading ? <AdPerformanceTable data={[]} isLoading={true} currentPage={currentPage} setCurrentPage={setCurrentPage} rowsPerPage={ROWS_PER_PAGE} totalRows={0} loadingProgress={loadingProgress} /> : paginatedData.length > 0 ? <AdPerformanceTable data={paginatedData} isLoading={isDataLoading} currentPage={currentPage} setCurrentPage={setCurrentPage} rowsPerPage={ROWS_PER_PAGE} totalRows={filteredData.length} loadingProgress={loadingProgress} /> : <div className="text-center py-8 text-gray-400">No ad performance data found for the selected criteria.</div>)}
      </div>

      {/* Row 3: Commission Tier Breakdown */}
      <div className="bg-white/5 border border-white/10 rounded-xl shadow-lg p-6 backdrop-blur-xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Award className="h-6 w-6 text-[#B8460E]" />
              Performance by Commission Tier
            </h3>
            <p className="text-sm text-gray-400">Your ads performance broken down by commission tier</p>
          </div>
        </div>

        {/* Commission Tier Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {isDataLoading ? (
            // Loading state for commission tier cards
            <>
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <div key={`loading-tier-${index}`} className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl transition-all duration-300 relative overflow-hidden">
                  <div className="flex items-center justify-center space-x-2 h-20">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#B8460E]"></div>
                    <span className="text-sm font-semibold text-gray-400">Loading tier data...</span>
                  </div>
                </div>
              ))}
            </>
          ) : (
            commissionTierBreakdown.tierBreakdown.map((tier, index) => {
              // Determine performance level based on ROAS
              const getPerformanceLevel = (minROAS) => {
                if (minROAS >= 2.5) return "top";
                if (minROAS >= 1.5) return "medium";
                return "low";
              };

              // Get color scheme based on performance level
              const getPerformanceColors = (level) => {
                switch (level) {
                  case "top":
                    return {
                      background: "bg-gradient-to-br from-orange-500/20 via-red-500/15 to-orange-600/20",
                      border: "border-orange-400/40",
                      hover: "hover:bg-gradient-to-br hover:from-orange-500/30 hover:via-red-500/25 hover:to-orange-600/30",
                      badge: "bg-gradient-to-r from-orange-500/30 to-red-500/30 text-orange-200 border border-orange-400/30",
                      glow: "shadow-lg shadow-orange-500/20",
                      shimmer: "bg-gradient-to-r from-transparent via-orange-300/20 to-transparent",
                      animatedBg: "bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-600/10",
                      earnedColor: "text-orange-300",
                    };
                  case "medium":
                    return {
                      background: "bg-gradient-to-br from-amber-500/10 via-yellow-500/8 to-amber-600/10",
                      border: "border-amber-400/25",
                      hover: "hover:bg-gradient-to-br hover:from-amber-500/15 hover:via-yellow-500/12 hover:to-amber-600/15",
                      badge: "bg-amber-500/20 text-amber-300 border border-amber-400/20",
                      glow: "shadow-md shadow-amber-500/10",
                      shimmer: "bg-gradient-to-r from-transparent via-amber-300/15 to-transparent",
                      animatedBg: "bg-gradient-to-r from-amber-500/8 via-yellow-500/6 to-amber-600/8",
                      earnedColor: "text-amber-300",
                    };
                  case "low":
                  default:
                    return {
                      background: "bg-gradient-to-br from-gray-500/5 via-slate-500/3 to-gray-600/5",
                      border: "border-gray-400/15",
                      hover: "hover:bg-gradient-to-br hover:from-gray-500/8 hover:via-slate-500/5 hover:to-gray-600/8",
                      badge: "bg-gray-500/15 text-gray-400 border border-gray-400/15",
                      glow: "shadow-sm shadow-gray-500/5",
                      shimmer: "bg-gradient-to-r from-transparent via-gray-300/10 to-transparent",
                      animatedBg: "bg-gradient-to-r from-gray-500/3 via-slate-500/2 to-gray-600/3",
                      earnedColor: "text-gray-400",
                    };
                }
              };

              const performanceLevel = getPerformanceLevel(tier.minROAS);
              const colors = getPerformanceColors(performanceLevel);

              return (
                <div key={tier.label} className={`${colors.background} border ${colors.border} rounded-lg p-4 backdrop-blur-xl ${colors.hover} transition-all duration-300 relative overflow-hidden ${colors.glow} group`}>
                  {/* Shimmer effect */}
                  <div
                    className={`absolute inset-0 ${colors.shimmer} rounded-lg pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                    style={{
                      animation: `shimmer 3s ease-in-out infinite ${index * 0.5}s`,
                    }}
                  ></div>

                  {/* Tier badge */}
                  <div className="absolute top-3 right-3 z-10">
                    <div className={`${colors.badge} px-2 py-1 rounded-full text-xs font-semibold backdrop-blur-sm`}>{tier.label}</div>
                  </div>

                  {/* Tier header */}
                  <div className="mb-3 relative z-10">
                    <h4 className={`text-lg font-semibold mb-1 ${performanceLevel === "top" ? "text-orange-200" : performanceLevel === "medium" ? "text-amber-200" : "text-gray-300"}`}>{tier.label} Commission</h4>
                    <p className={`text-sm ${performanceLevel === "top" ? "text-orange-300/70" : performanceLevel === "medium" ? "text-amber-300/70" : "text-gray-500"}`}>ROAS {tier.roasRange}</p>
                  </div>

                  {/* Tier stats */}
                  <div className="space-y-2 relative z-10">
                    <div className="flex justify-between">
                      <span className={`text-sm ${performanceLevel === "top" ? "text-orange-300/70" : performanceLevel === "medium" ? "text-amber-300/70" : "text-gray-500"}`}>Ads:</span>
                      <span className={`text-sm font-medium ${performanceLevel === "top" ? "text-orange-200" : performanceLevel === "medium" ? "text-amber-200" : "text-gray-400"}`}>{tier.adCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={`text-sm ${performanceLevel === "top" ? "text-orange-300/70" : performanceLevel === "medium" ? "text-amber-300/70" : "text-gray-500"}`}>Spend:</span>
                      <span className={`text-sm font-medium ${performanceLevel === "top" ? "text-orange-200" : performanceLevel === "medium" ? "text-amber-200" : "text-gray-400"}`}>{formatCurrency(tier.totalSpend)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={`text-sm ${performanceLevel === "top" ? "text-orange-300/70" : performanceLevel === "medium" ? "text-amber-300/70" : "text-gray-500"}`}>Earned:</span>
                      <span className={`text-sm font-medium ${colors.earnedColor}`}>{formatCurrency(tier.totalEarnings)}</span>
                    </div>
                  </div>

                  {/* Animated background */}
                  <div className={`absolute inset-0 ${colors.animatedBg} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg`}></div>
                </div>
              );
            })
          )}
        </div>

        {/* Commission Tier Structure */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-6 backdrop-blur-xl">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-400" />
            Commission Tier Structure
            <span className="text-xs font-normal text-gray-400">(Per-Ad ROAS)</span>
          </h4>
          {commissionTiersError && (
            <div className="mb-3 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2">
              Live commission tiers could not be loaded ({commissionTiersError}). Showing fallback rates.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {commissionTiersLoading ? (
              <div className="col-span-full flex items-center gap-2 text-sm text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#B8460E]"></div>
                Refreshing commission tiers...
              </div>
            ) : (
              activeCommissionTiers.map((tier, index) => {
              const nextTier = activeCommissionTiers[index + 1];
              const roasRange = nextTier ? `${tier.minROAS.toFixed(1)} - ${nextTier.minROAS.toFixed(1)}` : `${tier.minROAS.toFixed(1)} - ∞`;

              return (
                <div key={tier.label} className="flex justify-between items-center py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-[#B8460E] rounded-full"></div>
                    <span className="text-sm text-gray-400">ROAS {roasRange}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white">{tier.label}</span>
                    <span className="text-xs text-gray-400">commission</span>
                  </div>
                </div>
              );
              })
            )}
            <div className="text-xs text-gray-500 mt-2">* Commission is calculated per ad based on each ad's individual ROAS. Ads with ROAS below 0.8 do not qualify.</div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {isDataLoading ? (
            // Loading state for summary statistics
            <>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#B8460E]"></div>
                  <span className="text-lg font-semibold text-gray-400">Loading...</span>
                </div>
                <div className="text-sm text-gray-400 mt-2">Qualifying Ads</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#B8460E]"></div>
                  <span className="text-lg font-semibold text-gray-400">Loading...</span>
                </div>
                <div className="text-sm text-gray-400 mt-2">Avg Commission Rate</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl text-center">
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#B8460E]"></div>
                  <span className="text-lg font-semibold text-gray-400">Loading...</span>
                </div>
                <div className="text-sm text-gray-400 mt-2">Total Earnings</div>
              </div>
            </>
          ) : (
            // Normal state for summary statistics
            <>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl text-center">
                <div className="text-2xl font-bold text-white mb-1">{commissionTierBreakdown.summary.qualifyingAds}</div>
                <div className="text-sm text-gray-400">Qualifying Ads</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl text-center">
                <div className="text-2xl font-bold text-white mb-1">{commissionTierBreakdown.summary.averageCommissionRate.toFixed(2)}%</div>
                <div className="text-sm text-gray-400">Avg Commission Rate</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-xl text-center">
                <div className="text-2xl font-bold text-green-400 mb-1">{formatCurrency(commissionTierBreakdown.summary.totalEarnings)}</div>
                <div className="text-sm text-gray-400">Total Earnings</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformancePage;
